import React, { memo, useCallback, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Play, Copy, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface InputNodeData {
  label: string;
  userQuery: string;
}

interface InputNodeProps extends NodeProps {
  data: InputNodeData;
}

export const InputNode = memo(({ data, selected, id }: InputNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  
  const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            userQuery: newValue
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
      // toast({
      //   title: "Node duplicated",
      //   description: "The node has been duplicated successfully",
      // });
    }
  }, [id, getNodes, setNodes]);


  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    // Remove the node
    const updatedNodes = nodes.filter(node => node.id !== id);
    
    // Remove any edges connected to this node
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
    
    // toast({
    //   title: "Node deleted",
    //   description: "The node and its connections have been removed",
    // });
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Gumloop-style Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[280px] backdrop-blur-md bg-background/75 border-green-500/30 transition-all duration-200 ${
          selected 
            ? 'ring-2 ring-green-500/50 shadow-lg shadow-green-500/20' 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        {/* Header */}
        <div className="p-4 pb-2">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Play className="w-4 h-4 text-green-400" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm text-foreground">
                {data.label || 'Input'}
              </div>
              <div className="text-xs text-muted-foreground">
                Workflow entry point
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          <div className="space-y-2">
            <Label htmlFor={`input-${id}`} className="text-xs font-medium">
              User Query
            </Label>
            <Textarea
              id={`input-${id}`}
              placeholder="Enter your query or request..."
              value={data.userQuery || ''}
              onChange={handleQueryChange}
              className="min-h-[80px] resize-none text-sm bg-background/50 border-border/50 focus:border-green-500/50 focus:ring-green-500/20"
              style={{ fontSize: '13px' }}
            />
          </div>
        </div>
        
        {/* Output Handle - Positioned on right edge */}
        <Handle
          type="source"
          position={Position.Right}
          id="output"
          className="custom-handle"
          style={{
            backgroundColor: '#22c55e',
            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
          }}
        />
      </Card>
    </div>
  );
});

InputNode.displayName = 'InputNode';
